// Challenge progress tracking utilities

/**
 * Check if an item matches a challenge's criteria
 * @param {Object} item - The item being added to collection
 * @param {Object} challenge - The challenge to check against
 * @returns {boolean} Whether the item matches the challenge
 */
const doesItemMatchChallenge = (item, challenge) => {
  switch (challenge.type) {
    case 'any_collection':
      return true; // Any item counts

    case 'horror_movies':
      return (
        item.genre?.toLowerCase().includes('horror') ||
        item.genres?.some(g => g.toLowerCase().includes('horror'))
      );

    case 'decade_1980s':
      const year = parseInt(item.year || item.release_year || 0);
      return year >= 1980 && year <= 1989;

    case 'decade_1990s':
      const year90s = parseInt(item.year || item.release_year || 0);
      return year90s >= 1990 && year90s <= 1999;

    case 'action_movies':
      return (
        item.genre?.toLowerCase().includes('action') ||
        item.genres?.some(g => g.toLowerCase().includes('action'))
      );

    case 'comedy_movies':
      return (
        item.genre?.toLowerCase().includes('comedy') ||
        item.genres?.some(g => g.toLowerCase().includes('comedy'))
      );

    case 'sci_fi_movies':
      return (
        item.genre?.toLowerCase().includes('sci-fi') ||
        item.genre?.toLowerCase().includes('science fiction') ||
        item.genres?.some(
          g => g.toLowerCase().includes('sci-fi') || g.toLowerCase().includes('science fiction')
        )
      );

    default:
      return true; // Default to allowing all items
  }
};

/**
 * Update challenge progress for a user when they add an item to their collection
 * @param {Object} supabase - Supabase client
 * @param {string} userId - User ID
 * @param {Object} newItem - The item that was added to collection
 */
export const updateChallengeProgress = async (supabase, userId, newItem) => {
  try {
    const { data: challenges, error: challengeError } = await supabase
      .from('challenges')
      .select('*')
      .eq('is_active', true);

    if (challengeError || !challenges?.length) {
      return;
    }

    for (const challenge of challenges) {
      if (doesItemMatchChallenge(newItem, challenge)) {
        await ensureUserParticipationAndUpdateProgress(supabase, userId, challenge, newItem);
      }
    }
  } catch (error) {
    console.error('Error updating challenge progress:', error);
  }
};

/**
 * Ensure user is participating in challenge and update their progress
 */
const ensureUserParticipationAndUpdateProgress = async (supabase, userId, challenge, newItem) => {
  try {
    // Check if user is already participating
    let { data: participation, error: participationError } = await supabase
      .from('challenge_participants')
      .select('*')
      .eq('user_id', userId)
      .eq('challenge_id', challenge.id)
      .maybeSingle();

    if (participationError) {
      console.error('Error checking participation:', participationError);
      return;
    }

    // If user is not participating, auto-join them
    if (!participation) {
      const { data: newParticipation, error: joinError } = await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challenge.id,
          user_id: userId,
          progress: {
            current_count: 0,
            target_count: parseInt(challenge.target),
            items_added: [],
            last_updated: new Date().toISOString(),
          },
          is_completed: false,
        })
        .select()
        .single();

      if (joinError) {
        console.error('Error auto-joining challenge:', joinError);
        return;
      }

      participation = newParticipation;
    }

    // Update progress
    await updateCollectionChallengeProgress(supabase, participation, challenge, newItem);
  } catch (error) {
    console.error('Error ensuring participation and updating progress:', error);
  }
};

/**
 * Update progress for collection goal challenges
 */
const updateCollectionChallengeProgress = async (supabase, participation, challenge, newItem) => {
  try {
    const targetCount = parseInt(challenge.target);
    const currentProgress = participation.progress || {};

    // Initialize progress structure if empty
    const updatedProgress = {
      current_count: currentProgress.current_count || 0,
      target_count: targetCount,
      items_added: currentProgress.items_added || [],
      last_updated: new Date().toISOString(),
    };

    // Add the new item to the progress
    const newItemEntry = {
      id: newItem.id,
      title: newItem.title || newItem.movie_title || 'Unknown Title',
      date_added: new Date().toISOString().split('T')[0],
    };

    // Check if item already exists (prevent duplicates)
    const itemExists = updatedProgress.items_added.some(item => item.id === newItem.id);
    if (!itemExists) {
      updatedProgress.items_added.push(newItemEntry);
      updatedProgress.current_count = updatedProgress.items_added.length;
    }

    // Check if challenge is completed
    const isCompleted = updatedProgress.current_count >= targetCount;
    const completedAt = isCompleted ? new Date().toISOString() : null;

    // Update the database
    const { error: updateError } = await supabase
      .from('challenge_participants')
      .update({
        progress: updatedProgress,
        is_completed: isCompleted,
        completed_at: completedAt,
      })
      .eq('id', participation.id);

    if (updateError) {
      console.error('Error updating challenge progress:', updateError);
    } else if (isCompleted) {
      console.log(`Challenge "${challenge.title}" completed by user!`);
      // You could add a notification system here
    }
  } catch (error) {
    console.error('Error updating collection challenge progress:', error);
  }
};

/**
 * Get user's progress for a specific challenge
 * @param {Object} supabase - Supabase client
 * @param {string} userId - User ID
 * @param {string} challengeId - Challenge ID
 * @returns {Object} Progress object or null
 */
export const getUserChallengeProgress = async (supabase, userId, challengeId) => {
  try {
    const { data, error } = await supabase
      .from('challenge_participants')
      .select('progress, is_completed, completed_at')
      .eq('user_id', userId)
      .eq('challenge_id', challengeId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user challenge progress:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching user challenge progress:', error);
    return null;
  }
};

/**
 * Format progress for display
 * @param {Object} progress - Progress object from database
 * @returns {string} Formatted progress string
 */
export const formatChallengeProgress = progress => {
  if (!progress || !progress.current_count) {
    return '0 / 0';
  }

  return `${progress.current_count} / ${progress.target_count}`;
};

/**
 * Calculate progress percentage
 * @param {Object} progress - Progress object from database
 * @returns {number} Percentage (0-100)
 */
export const calculateProgressPercentage = progress => {
  if (!progress || !progress.target_count || progress.target_count === 0) {
    return 0;
  }

  const percentage = (progress.current_count / progress.target_count) * 100;
  return Math.min(percentage, 100); // Cap at 100%
};
